<template>
    <ui-modal
        :load="loader.loadSimulationProduct"
        :load-success="loader.loadSuccessSimulationProduct"
        :show-close-option="false"
        name="simulation-product"
        title="Selecionar o empreendimento"
        icon="fal fa-building"
        size="xs"
    >
        <div v-if="loader.products" class="load-indicator">
            <ui-load-indicator bg-color="#e7e9f4" color="#69D296" :h="50" :stroke="8" />
        </div>

        <form v-else class="form" @submit.prevent="submit">
            <div class="modal-content">
                <ui-overlay-load v-if="loader.editingForm" />

                <ui-select-2
                    v-model="form.data.product_id"
                    :data="productOptions"
                    :loading="loader.selectProduct"
                    :label="'Empreendimento:'"
                    item-label="name"
                    return-value="id"
                    @input="selectProductHandler"
                />

                <!-- Formulário sem unidades -->
                <fieldset v-if="layout.showStaticForm">
                    <div class="form-row">
                        <div class="col">
                            <ui-input
                                v-model="form.data.unity"
                                :disabled="!layout.showStaticForm || hasProductInLaunchday"
                                label="Unidade:"
                            />
                        </div>

                        <div class="col">
                            <ui-input-money
                                v-model="form.data.unity_cost"
                                :disabled="!layout.showStaticForm || hasProductInLaunchday"
                                label="Valor da unidade:"
                                prefix=''
                                group
                            />
                        </div>
                    </div>
                </fieldset>

                <!-- Formulário com tabela de vendas -->
                <fieldset v-else-if="layout.showDynamicForm">
                    <div class="form-row">
                        <div class="col">
                            <ui-select-2
                                v-model="form.data.block"
                                :data="blockOptions"
                                :loading="loader.selectProductBlock"
                                :disabled="!layout.showDynamicForm"
                                :label="hideNumberAndBlock ? '-' : 'Bloco / Quadra:'"
                                item-label="label"
                                return-value="value"
                                @input="selectProductBlockHandler"
                            />
                        </div>

                        <div v-if="layout.showSelectFloor" class="col">
                            <ui-select-2
                                v-model="form.data.floor"
                                :data="floorOptionsComputed"
                                :loading="loader.selectProductFloor"
                                :disabled="form.fields.floor.disabled || !layout.showSelectFloor"
                                :label="'Andar:'"
                                item-label="label"
                                return-value="value"
                                @input="selectProductFloorHandler"
                            />
                        </div>
                    </div>
                    <div v-if="showCustomValue" class="col">
                        <ui-input-money
                            v-model="form.data.custom_unity_cost"
                            v-bind="moneyConfig"
                            :disabled="!layout.showDynamicForm"
                            :required="true"
                            :label="'Valor da unidade:'"
                            group />
                    </div>
                </fieldset>
            </div>

            <div v-if="layout.showDynamicForm && layout.showSelectUnity">
                <div class="select-unity">
                    <ui-select-unity
                        v-model="unityOptionsComputed"
                        :default="form.data.unity_id"
                        :product="form.data.product"
                        :show-reserved="form.restrictSituation"
                        :load="loader.selectProductUnity"/>
                </div>
            </div>

            <div v-if="layout.showStaticFormRate" class="modal-content shadow-area">
                <div class="input-tax" >
                    <ui-input
                        v-model="form.data.monthly_rate"
                        :disabled="form.fields.monthly_rate.disabled"
                        label="Juros:"
                        type="tel"
                        group="%"
                        percent />
                </div>
            </div>
        </form>

        <template #footer>
            <div class="actions">
                <ui-switch
                    v-if="layout.showSwitchUnitySituation || (user && user.user_group == 'admin')"
                    id="unitySituation"
                    :checked="form.restrictSituation"
                    label="Unidades reservadas"
                    name="unitySituation"
                    design="xs"
                    @input="unitySituationHandler"
                />
            </div>

            <div class="buttons">
                <ui-button design="link" @click="cancel">Cancelar</ui-button>
                <ui-button
                    :disabled="layout.disableButtonSubmit"
                    :loading="loader.buttonSubmit"
                    design="lg success mw-100"
                    @click="submit">
                    Avançar <i class="fal fa-long-arrow-right" />
                </ui-button>
            </div>
        </template>
    </ui-modal>
</template>

<script>
    import { uiLoadIndicator, uiOverlayLoad } from '@/components/ui'
import uiSelectUnity from '@/components/ui-facilita/sales/select-unity'
import { uiButton } from "@/components/ui/buttons"
import { uiInput, uiInputMoney, uiSelect2, uiSwitch } from "@/components/ui/form"
import { uiModal } from "@/components/ui/modal"
import { product } from '@/services/axios'
import { storage } from '@/services/storage'

    import { mapGetters } from 'vuex'

    const defaultLayout = () => {
        return {
            showStaticForm: false,
            showStaticFormRate: false,
            showDynamicForm: false,
            showSelectUnity: false,
            showSelectFloor: false,
            disableButtonSubmit: true,
            showSwitchUnitySituation: false,
        }
    }

    const defaultLoader = () => {
        return {
            products: true,
            editingForm: false,
            loadSimulationProduct: false,
            loadSuccessSimulationProduct: false,
            selectProduct: false,
            selectProductBlock: false,
            selectProductFloor: false,
            selectProductUnity: false,
            buttonSubmit: false,
        }
    }

    const defaultFormData = () => {
        return {
            product_id: null,
            product: null,
            block: null,
            floor: null,
            unity_id: null,
            unity: null,
            unity_cost: 0,
            custom_unity_cost: 0,
            monthly_rate: null
        }
    }

    export default {
        name: "ui-product-select-modal",

        components: {
            uiButton,
            uiInput,
            uiSelect2,
            uiSwitch,
            uiInputMoney,
            uiModal,
            uiOverlayLoad,
            uiLoadIndicator,
            uiSelectUnity,
        },

        data() {
            return {
                layout: { ...defaultLayout() },
                loader: { ...defaultLoader() },
                user: null,
                form: {
                    fields: {
                        floor: {
                            disabled: true,
                        },
                        monthly_rate: {
                            disabled: true
                        }
                    },
                    data: { ...defaultFormData() },
                    restrictSituation: false
                },
                productOptions: [],
                blockOptions: [],
                floorOptions: [],
                unityOptions: [],
                hasProductInLaunchday: false,
                hasLaunchday: null,
                hideNumberAndBlock: false,
                showCustomValue: false,
                moneyConfig: {
                    decimal: ',',
                    thousands: '.',
                    prefix: 'R$ ',
                    suffix: ' ',
                    precision: 2,
                    masked: false
                },
            }
        },

        computed: {
            ...mapGetters('simulation', {
                unity: 'getUnity',
                product: 'getProduct',
                salesTable: 'getSalesTable',
                deal: 'getCustomerDeal',
                unityCost: 'getUnityCost',
                customValueGetter: 'getCustomValue'
            }),
            customValue: {
                get() {
                    return this.customValueGetter;
                },
                set(value) {
                    this.$store.dispatch('simulation/updateCustomValue', value);
                }
            },
            /**
             * Retorna a configuração da etapa 2 que define se o usuário atual pode ou
             * não alterar e utilizar a taxa de juros vinda da etapa 2
             */
            salesTableConfigComputed() {
                const product = this.form.data.product

                return product?.availability?.infos?.sales_table?.rules
            },

            /**
             * Computed property que filtra os andares de acordo com o bloco/quadra ou torre
             * selecionada
             */
            floorOptionsComputed() {
                let options = []
                if (this.form.data.block) {
                    options = this.floorOptions.filter(floor => floor.block === this.form.data.block)
                }

                return options
            },

            /**
             * Computed property que filtra ou não as unidades de acordo com a situação e
             * o switch de configuração
             */
            unityOptionsComputed: {
                get() {
                    let restrictions = ['available']

                    /** Verifica se irá filtrar unidades reservadas e pré-reservadas */
                    if (this.form.restrictSituation) {
                        restrictions.push('reserved')

                        /**
                         * Verifica se a fila de reservas está ativa para filtrar as unidades
                         * pré-reservadas
                         */
                        const product = this.form.data.product
                        if (product?.availability?.infos?.reserve?.queueReserve?.status === 'active') {
                            restrictions.push('pre-reserve')
                        }
                    }
                    return this.unityOptions.filter(unity => restrictions.includes(unity.situation))
                },
                set(value) {
                    if(value) {
                        const unity = this.unityOptions.find(unity => unity.id === parseInt(value))
                        unity.cost == null || parseInt(unity.cost) == 0 ? this.showCustomValue = true : this.showCustomValue = false;
                        if (unity && unity.id) {
                            this.$set(this.form.data, 'unity', unity)
                            this.$set(this.form.data, 'unity_id', unity.id)
                            this.$set(this.form.data, 'unity_cost', this.sanitizeValue(this.form.data?.unity?.cost) ? 0 : this.unityCost)
                        } else {
                            this.$toastr.e('A unidade selecionada não existe')
                        }
                    } else {
                        this.$toastr.e('erro')
                    }
                }
            },
            /**
             * Verifica se os campos obrigatórios estão preenchidos
             */
            isFormValidComputed() {
                let isStaticFormValid = false
                let isDynamicFormValid = false
                let isRateFormValid = false
                /** Verifica se o form é o estático e se os respectivos campos foram preenchidos */
                isStaticFormValid = this.layout.showStaticForm ? this.form.data.unity && this.form.data.unity_cost : false
                /** Trata os valores que são receb
                /** Verifica se o form é o dinamico e se os respectivos campos foram preenchidos */
                if (this.layout.showDynamicForm) {
                    const sanitizedCost = !!this.sanitizeValue(this.form.data?.unity?.cost);
                    const sanitizedUnityCost = !!this.sanitizeValue(this.form.data.custom_unity_cost);
                    // Formulário dinâmico é válido se:
                    // 1. Existe `block` e `floor`, e pelo menos `sanitizedCost` ou `sanitizedUnityCost` está presente
                    // 2. Ou existe `block` e `unity`, e pelo menos `sanitizedCost` ou `sanitizedUnityCost` está presente
                    isDynamicFormValid =
                        (this.form.data.block && this.form.data.floor && (sanitizedCost || sanitizedUnityCost)) ||
                        (this.form.data.block && this.form.data.unity && (sanitizedCost || sanitizedUnityCost));
                }
                /** Verifica se o tem o campo de juros e se está preenchido */
                isRateFormValid = this.layout.showStaticFormRate ? this.form.data.monthly_rate : false
                return (this.layout.showStaticFormRate ? isDynamicFormValid && isRateFormValid : isDynamicFormValid) || // Verifica se tem juros(obrigatório) se sim valida os juros e o form dinamico, se não valida apenas o form dinamico
                    (this.layout.showStaticFormRate ? isStaticFormValid && isRateFormValid : isStaticFormValid) // Verifica se tem juros(obrigatório) se sim valida os juros e o form estático, se não valida apenas o form estático
            },
        },

        watch: {
            /**
             * Watcher para observar o estado de validação do formulário
             */
            isFormValidComputed(newValue) {
                /** Verifica se  o formulário é valido. Caso seja, habilita o botão de avançar */
                if (newValue) {
                    this.$set(this.layout, 'disableButtonSubmit', false)
                } else {
                    this.$set(this.layout, 'disableButtonSubmit', true)
                }
            }
        },

        created() {
            /**
             * Ao Abrir o modal pela primeira vez, envia-se um objeto como parametro
             * para ocultar o botão de cancelar.
             */
            this.$root.$on("modal-open-simulation-product", async () => {
                this.resetLayout()
                this.resetLoader()
                await this.loadProducts()
			})

            this.$root.$on('modal-close-simulation-product', () => {
                this.user = JSON.parse(storage.get('user_logged'))
                // const user = { ...this.$store.state.auth.user }
                const storageKey = `guide_simulationUnity_${this.user.id}`
                const unitySimulation = JSON.parse(storage.getLegacy(storageKey))

                storage.removeLegacy(storageKey);
            })

            if(this.hasProductInLaunchday) this.$set(this.form, 'restrictSituation', true)

            this.user = JSON.parse(storage.get('user_logged'))
            let url = window.location.pathname
            let parts = url.split('/')
            let lastPart = parts.pop()

            if(isNaN(lastPart)) {
                storage.removeLegacy(`guide_has_launchday_${this.user.id}`)
            }
            else {
                this.hasLaunchday = JSON.parse(storage.getLegacy(`guide_has_launchday_${this.user.id}`))
            }
        },
        beforeDestroy() {
            this.$root.$off("modal-close-simulation-product")
        },

        methods: {
            sanitizeValue(value) {
                // Converte para string e remove espaços
                let sanitizedValue = String(value).trim();

                // Se o valor for "0", "0.00", ou vazio, retorna 0
                if (sanitizedValue === "0" || sanitizedValue === "0.00" || sanitizedValue === "") {
                    return 0;
                }

                // Se for um número válido, retorna como número
                let parsedValue = parseFloat(sanitizedValue);
                if (!isNaN(parsedValue)) {
                    return parsedValue;
                }

                // Se não for número, retorna o valor original
                return value;
            },
            hasValue(value) {
				if (value == null || value.length == 0) return false
				if (value) return true
				return false
			},

            setList(obj, list, value) {
                list.forEach((item) => {
                    obj[item] = value
                })
            },

            /**
             * Emite um evento no root para fechar o modal e volta para o último histórico
             */
            cancel() {
                this.$root.$emit('modal-close-simulation-product')
                if(!(this.product && this.product.id))
                    window.history.back()
            },

            /**
             * Seta os dados do formulário de acordo com o state do vuex
             */
            async setFormData() {
                /** Verifica se existe um produto e uma unidade selecionados no vuex */
                if (this.product && this.product.id) {
                    /** Seta os dados do produto e da unidade */
                    this.form.data.product_id = this.product.id
                    this.form.data.product = { ...this.product }
                    await this.selectProductHandler()

                    /**
                     * Carrega as opções de quadra/torre e andar de acordo com a disponibilidade
                     * do produto
                     */
                    this.setForm()

                    /** Caso não haja uma unidade já selecionada, força a seleção */
                    if (!(this.unity && this.unity.id)) return
                    this.form.data.unity_id = this.unity.id
                    this.form.data.unity = this.hasProductInLaunchday ? null : (this.unity && Object.keys(this.unity).length ? { ...this.unity } : null)
                    //Atribui 0 para o unity_cost que é do input de valor customizado pra caso unidades não tenham valor pra fazer simulação
                    //Caso a unidade tenha o valor, retorna 0 para o unity cost
                    this.form.data.unity_cost = this.form.data.unity.cost ? 0 : this.unityCost;
                    /** Caso a unidade selecionada esteja reservada, mostra todas as unidades reservadas */
                    if (this.form.data.unity && this.form.data.unity.situation === 'reserved') {
                        this.$set(this.form, 'restrictSituation', true)
                    }

                    /**
                     * Carrega as unidade de acordo com os dados da unidade e disponibilidade do
                     * produto
                     */
                    if (this.form.data.product.availability.id) {
                        switch (this.form.data.product.availability.type) {
                            case 'vertical':
                                this.form.data.block = this.unity.block
                                this.form.data.floor = this.unity.floor
                                this.$set(this.form.fields.floor, 'disabled', false)
                                await this.loadUnits(this.unity.floor, this.unity.block)
                                break

                            default:
                                this.form.data.block = `${this.unity.pos_y}_${this.unity.stage}`
                                this.form.data.floor = null
                                await this.loadUnits(this.unity.pos_y, this.unity.stage)
                                break
                        }
                    } else {
                        this.$toastr.e('O produto selecionado não possui uma disponibilidade')
                    }
                }
            },

            /**
             * Busca os produtos cadastrados
             */
            async loadProducts() {
                this.$set(this.loader, 'products', true)
                this.$set(this.form, 'restrictSituation', false)
                this.$set(this.layout, 'showSwitchUnitySituation', false)

                try {
                    const response = await product.getProductWithoutAvailable({ withAvailability: 1 })
                    this.productOptions = [ ...response.data ]

                    // Após carregar os produtos, definir o produto salvo no Local Storage, se existir
                    this.user = JSON.parse(storage.get('user_logged'))
                    const storageKey = `guide_simulationUnity_${this.user.id}`
                    const unitySimulation = JSON.parse(storage.getLegacy(storageKey))
                    if (unitySimulation && unitySimulation.step2 && unitySimulation.step2.product_id) {
                        const savedProductId = unitySimulation.step2.product_id
                        const existsInOptions = this.productOptions.some(p => p.id === savedProductId)

                        if (existsInOptions) {
                            this.form.data.product_id = savedProductId
                        }
                    }

                    this.$set(this.loader, 'products', false)
                    await this.setFormData()
                } catch(error){
                    if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                    this.$set(this.loader, 'products', false)
                }
            },

            /**
             * Recebe dois parâmetros para buscar as unidades via requisição AJAX
             *
             * @param param1 Quando a disponibilidade for do tipo vertical é o andar e quando horizontal é o posY ou bloco/quadra
             * @param param2 Quando a disponibilidade for do tipo vertical é a torre e quando horizontal é o stage
             */
            async loadUnits(param1, param2) {
                this.$set(this.loader, 'selectProductUnity', true)

                try {
                    const response = await product.getBlockUnits(this.form.data.product_id, this.form.data.product.availability.type, param1, param2, this.hasLaunchday && this.hasLaunchday.launch ? this.deal.id : null)
                    this.unityOptions = [...response.data]
                    this.$set(this.loader, 'selectProductUnity', false)
                    this.$set(this.layout, 'showSelectUnity', true)
                } catch(error) {
                    if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
                }
            },

            /**
             * Mostra o formulário estático
             */
            showStaticForm() {
                this.$set(this.layout, 'showStaticForm', true)
                this.$set(this.layout, 'showDynamicForm', false)
            },

            /**
             * Mostra o formulário dinâmico
             */
            showDynamicForm() {
                this.$set(this.layout, 'showStaticForm', false)
                this.$set(this.layout, 'showDynamicForm', true)
            },

            /**
             * Reseta o layout
             */
            resetLayout() {
                this.layout = {...defaultLayout()}
            },

            /**
             * Reseta os loadings
             */
            resetLoader() {
                this.loader = {...defaultLoader()}
            },

            /**
             * Carrega as opções dos selects de quadras/torre e andare, bem como os dados do formulário
             */
            setForm() {
                if (this.form.data.product && this.form.data.product.availability) {
                    const availability = { ...this.form.data.product.availability }
                    this.hideNumberAndBlock =  availability?.infos?.multiProperty?.hideNumberAndBlock == 'true'
                    if (availability.units && availability.units.length) {
                        this.blockOptions = []
                        this.floorOptions = []
                        /**
                         * Verifica se a fila de reservas está ativa, e se estiver habilita o toggle
                         * que permite listar as unidades reservadas
                         */
                        const queueReserveActive = availability?.infos?.reserve?.queueReserve?.status === 'active'
                        if (queueReserveActive) this.$set(this.layout, 'showSwitchUnitySituation', true)

                        switch (availability.type) {
                            case 'vertical':
                                for (const unity of availability.units) {
                                    /** Verifica se a torre e o andar já foram adicionados */
                                    if(!this.blockOptions[unity.block]) {
                                        this.$set(this.blockOptions, unity.block, { value: unity.block, label: `Torre ${unity.block}`})
                                    }

                                    if (!this.floorOptions[`${unity.block}_${unity.floor}`]) {
                                        this.$set(this.floorOptions,
                                            `${unity.block}_${unity.floor}`,
                                            { value: unity.floor, block: unity.block, label: parseInt(unity.floor) ? `Andar ${unity.floor}` : 'Térreo' }
                                        )
                                    }
                                }
                                this.$set(this.layout, 'showSelectFloor', true)
                                break

                            default:
                            for (const unity of availability.units) {
                                    // Verifica se a combinação de quadra e etapa já foi adicionada
                                    const key = `${unity.pos_y}_${unity.stage}`;
                                    if (this.blockOptions[key]) continue;

                                    /**
                                     * Verifica se tem stage para aprensentar na tela e caso não tenha
                                     * mostrar apenas a quadra
                                     */
                                    const renderLabel = () => {
                                        return !!unity.stage ? `Quadra ${unity.block} / Etapa ${unity.stage}` : `Quadra ${unity.block}`
                                    }

                                    const label = `${this.hideNumberAndBlock ? '' : renderLabel()}`;

                                    this.$set(this.blockOptions, key, { value: key, label: label });
                                }
                                break
                        }
                        this.showDynamicForm()
                        this.blockOptions = Object.values(this.blockOptions)
                        this.floorOptions = Object.values(this.floorOptions)

                    } else {
                        this.showStaticForm()
                    }

                    /** Verifica se a disponibilidade do produto não possui tabela de vendas */
                    if (!(availability.sales_table && Object.values(availability.sales_table).length)) {
                        let monthlyRate = 0
                        if (availability?.infos?.sales_table?.monthly_rate) {
                            monthlyRate = parseFloat(availability.infos.sales_table.monthly_rate)

                        } else if (this.salesTable.monthly_rate) {
                            /**
                             * Caso a disponibilidade não possua configurações da tabela de vendas, utiliza
                             * a última taxa de juros configurada
                             */
                            monthlyRate = parseFloat(this.salesTable.monthly_rate)
                        }
                        this.$set(this.form.data, 'monthly_rate', monthlyRate)

                        /**
                         * De acordo com a regra da tabela de vendas na etapa 2, decide se vai visualizar
                         * ou não a taxa de juros
                         */
                        switch (this.salesTableConfigComputed) {
                            case 'broker':
                            case 'active':
                                this.$set(this.layout, 'showStaticFormRate', true)
                                /** Permite alterar a taxa de juros, caso a configuração seja 'broker' */
                                this.$set(this.form.fields.monthly_rate, 'disabled', this.salesTableConfigComputed !== 'broker')
                                break

                            /** Não mostrar a taxa de juros */
                            default:
                                this.$set(this.layout, 'showStaticFormRate', false)
                        }
                    }
                }
            },

            /**
             * Handler que trata o evento de quando um produto é selecionado
             */
            async selectProductHandler() {
                this.$set(this, 'showCustomValue', false);
                this.$set(this.loader, 'selectProduct', true)
                this.setList(this.form.data, ['unity', 'unity_id', 'block', 'floor'], null)
                this.$set(this.form, 'restrictSituation', false)
                this.resetLayout()

                if(this.hasValue(this.form.data.product_id)) {
                    try {
                        const response = await product.getLimitedTableFlow(this.form.data.product_id, this.hasLaunchday && this.hasLaunchday.launch ? this.deal.id : null)
                        this.$set(this.form.data, 'product', { ...response.data})
                        if(response.data?.infos?.launchday_mode == "1") {
                            this.$set(this.form, 'restrictSituation', true)
                            this.hasProductInLaunchday = response.data?.availability?.units?.length == 0
                        }
                        this.loader.selectProduct = false
                        this.setForm()
                    } catch(error){
                        if (error?.data?.msg) this.$toastr.e(error.data.msg)
                    } finally {
                        this.loader.selectProduct = false
                    }
                } else {
                    setTimeout(() => {
                        this.resetLayout()
                        this.$set(this.loader, 'selectProduct', false)
                        this.$set(this.layout, 'showSwitchUnitySituation', false)
                    }, 404)
                }
            },

            /**
             * Handler que trata o evento de quando uma quadra/torre for selecionada
             */
            async selectProductBlockHandler() {
                //Faz Aqui
                //Melhorar a situação do custom unity cost e do unity cost, estão se embaralhando e perdendo valor na tela.
                //this.$store.dispatch('simulation/setUnityValue');
                this.$set(this.form.data, 'unity_cost', 0);
                this.$set(this.loader, 'selectProductBlock', true)
                this.$set(this.layout, 'showSelectUnity', false)
                this.setList(this.form.data, ['unity', 'unity_id', 'floor'], null)
                this.$set(this.form.fields.floor, 'disabled', true)

                if(this.hasValue(this.form.data.block)) {
                    if (this.form.data.product.availability.type == 'horizontal'){
                        const array = this.form.data.block.split('_')
                        const posY = array.length ? array[0] : ''
                        const stage = array.length ? array[1] : ''
                        await this.loadUnits(posY, stage)
                    }
                    setTimeout(() => {
                        this.$set(this.form.fields.floor, 'disabled', false)
                        this.$set(this.loader, 'selectProductBlock', false)
                    }, 404)
                } else {
                    setTimeout(() => {
                        this.$set(this.form.fields.floor, 'disabled', true)
                        this.setList(this.layout, ['showSelectUnity', 'selectProductBlock'], false)
                        this.$set(this.loader, 'selectProductBlock', false)
                    }, 404)
                }
            },

            /**
             * Handler que trata o evento de quando um andar for selecionado
             */
            async selectProductFloorHandler() {
                this.$set(this, 'showCustomValue', false);
                if(!this.layout.showSelectUnity)
                    this.$set(this.loader, 'selectProductFloor', true)

                this.$set(this.layout, 'disableButtonSubmit', true)

                if(this.hasValue(this.form.data.floor)) {
                    await this.loadUnits(this.form.data.floor, this.form.data.block)
                    setTimeout(() => {
                        this.$set(this.loader, 'selectProductFloor', false)
                    }, 404)
                } else {
                    setTimeout(() => {
                        this.$set(this.layout, 'showSelectUnity', false)
                        this.$set(this.loader, 'selectProductFloor', false)
                        this.$set(this.loader, 'selectProductUnity', false)
                    }, 809)
                }
            },

            /**
             * Altera o filtro de Unidades mostrando ou não as unidades reservadas
             */
            unitySituationHandler(value) {
                this.$set(this.loader, 'selectProductUnity', true)
                this.$set(this.form, 'restrictSituation', value)
                this.$set(this.form.data, 'unity', null)

                setTimeout(() => {
                    this.$set(this.loader, 'selectProductUnity', false)
                }, 404)
            },

            /**
             * Fechar o modal, e mostrar a tela do simulador enviar os dados do modal para a tela
             */
            submit() {
                /**
                 * Emite o evento do Google Analytics
                 */
                if(this.form.data?.unity?.id && !this.sanitizeValue(this.form.data.unity.cost) && !this.sanitizeValue(this.form.data.custom_unity_cost)) return;
                if(this.hasProductInLaunchday) return
                this.$gtm.trackEvent({
                    event: "fac_button_click",
                    category: "vuejs-simular proposta",
                    action: "click",
                    label: "Primeiro botão avançar",
                })
                const data = { ...this.form.data };
                data.unity_cost = data.custom_unity_cost ? data.custom_unity_cost : data.unity_cost || 0;

                this.$set(this.form, 'data', { ...defaultFormData() })
                this.$set(this.loader, { ...defaultLoader() })
                this.$set(this.loader, 'loadSimulationProduct', true)
                this.resetLayout()
                // eslint-disable-next-line vue/valid-next-tick
                this.$nextTick()

                setTimeout(() => {
                    this.$emit('selected', data)
                    setTimeout(() => {
                        this.$set(this.loader, 'loadSuccessSimulationProduct', true)

                        setTimeout(() => {
                            this.cancel()
                        }, 404)

                    }, 809)

                }, 404)
                this.$store.dispatch('simulation/setUnityValue', data.custom_unity_cost || data.unity.cost);
                this.$set(this, 'showCustomValue', false);
            },
        }
    }
</script>

<style lang="stylus" scoped>
    @import './styles.styl'
</style>